import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { InviteOut } from './types';
import { subscriptionApi } from './api';
import { haptics } from '@/shared/lib/haptics';
import { useT } from '@/shared/lib/i18n';

interface Props {
  subscriptionId: string;
  invite: InviteOut | null;
  onRevoke?: () => void;
}

export function InviteLinkCard({ subscriptionId, invite, onRevoke }: Props) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  const copyMutation = useMutation({
    mutationFn: () => subscriptionApi.createInvite(subscriptionId),
    onSuccess: (data) => {
      navigator.clipboard.writeText(data.deep_link).catch(() => {});
      haptics.success();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async () => {
      // Revoke = POST a new invite (existing one is returned if active)
      // For proper revoke we'd need a DELETE endpoint — stub for now
      onRevoke?.();
    },
  });

  return (
    <div className="flex items-center gap-3 rounded-[14px] px-[14px] py-[11px] bg-white/40 border-[1.5px] border-dashed border-brand-green/45">
      {/* Link icon */}
      <svg
        width="16" height="16" viewBox="0 0 24 24" fill="none"
        stroke="#138a5b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        className="flex-shrink-0"
      >
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-[#138a5b]">
          {invite ? t('subscription.inviteLink.active') : t('subscription.inviteLink.none')}
        </p>
        {invite && (
          <p className="text-[11px] text-text-muted font-mono truncate mt-0.5">
            …?start=invite_{invite.code}
          </p>
        )}
      </div>

      {/* Buttons */}
      <button
        className="flex-shrink-0 text-[12px] font-semibold text-white bg-brand-gradient rounded-[8px] px-[11px] py-[5px] active:opacity-80"
        onClick={() => copyMutation.mutate()}
        disabled={copyMutation.isPending}
      >
        {copied ? t('subscription.inviteLink.copied') : copyMutation.isPending ? '…' : t('subscription.inviteLink.copy')}
      </button>

      {invite && (
        <button
          className="flex-shrink-0 text-[12px] font-semibold text-status-debt bg-[rgba(229,42,48,0.09)] border border-[rgba(193,7,16,0.28)] rounded-[8px] px-[11px] py-[5px] active:opacity-80"
          onClick={() => revokeMutation.mutate()}
          disabled={revokeMutation.isPending}
        >
          {t('subscription.inviteLink.revoke')}
        </button>
      )}
    </div>
  );
}
